import { useState } from 'react'
import { toast } from 'react-toastify'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

// styles
import styles from './styles.module.scss'

// utils
import {
  StudentForthStepType,
  studentFourthStepFormDataSchema,
  StudentFormType,
} from '../../../utils/schemas/studentFormDataSchema'
import { stringToDate } from '../../../utils/formatting/dateMask'

import { IRegisterStudent } from '../../../interfaces/types'

//hooks
import api from '../../../services/api'
import { USE_TERMS } from '../../../constants'
import { handleKeyUpMoney } from '../../../utils/formatting/money'
import { useValidateSchema } from '../../../hooks/useSchemaValidator'
import { AlunoRepository } from '../../admin/repositories/aluno.repository'

//components
import { Header } from '@/components/Header'
import { Input } from '../../../components/Input'
import { Modal } from '../../../components/Modal'
import { Loader } from '../../../components/Loader'
import { Select } from '../../../components/Select'
import { Checkbox } from '../../../components/Checkbox'
import { Statusbar } from '../../../components/Statusbar'
import { TitleFormStudents } from '../../../components/TitleForm'
import { FooterButtons } from '../../../components/FooterButtons'

interface IFormStudent {
  social_media: string
  pe_code: string
  family_income: string
}

export function StudentsFormStepFour() {
  const [loader, setLoader] = useState(true)
  const [error, setError] = useState('')
  const [openTerms, setOpenTerms] = useState(false)
  const [acceptedTerms, setAcceptedTerms] = useState(false)
  const { register, isSubmitting, errors, handleSubmit } = useValidateSchema(
    studentFourthStepFormDataSchema as unknown as StudentForthStepType,
  )
  const navigate = useNavigate()
  const { state } = useLocation()
  const { course_id } = useParams()
  const hasErrors =
    !!errors?.pe_code || !!errors?.family_income || !!errors?.social_media

  const formatIncome = (income: string) => {
    const value = income.replace('R$ ', '').replaceAll('.', '').replace(',', '')
    return +value
  }

  const formatData = (data: StudentFormType) => ({
    student: {
      alunocpf: data.cpf,
      alunonascimento: data?.date_of_birth,
      alunonome: data.name,
      code_pe: data?.pe_code || 'Sem indicação',
      conheceu: data.social_media,
      endereco: data.address,
      responsavelcpf: data.cpf_responsible,
      responsavelemail: data.email,
      responsavelnascimento: data?.date_of_birth_responsible,
      responsavelnome: data.name_responsible,
      responsaveltelefone: data.telephone,
      ultimaescola: data.current_school,
      friend_pe_id: data.friend_pe_id,
      accepted_terms: acceptedTerms,
      family_income: formatIncome(data.family_income),
      cep: data.cep,
      second_phone: data.second_phone,
    },
    course_id,
  })

  const getPeFriend = async (code_pe: string) => {
    const { data } = await api.get(`friends/by-code?code=${code_pe}`)

    if (!data?.friend) {
      toast('Código PE errado.', { type: 'error' })
      return
    }

    return data.friend
  }

  const registerStudent = (data: IRegisterStudent) =>
    new Promise(async (resolve, reject) => {
      const { register } = AlunoRepository.GetInstance()
      try {
        const response = await register(data)

        if (!hasErrors && response.status === 200) {
          navigate(`/register-student/${course_id}/success`)
          resolve(null)
        }
      } catch (error) {
        setError(
          error?.response?.data?.error ||
            error?.response?.data?.message?.message ||
            'Não foi possível realizar a inscrição',
        )
        reject(
          error?.response?.data?.error ||
            error?.response?.data?.message?.message,
        )
        setLoader(false)
      }
    })

  const handleOnSubmit = async (formData: IFormStudent) => {
    const dataStudent = {
      ...state?.student,
      ...state?.responsible,
      ...formData,
      responsavelnascimento: stringToDate(
        state.responsible.date_of_birth_responsible,
      ),
      alunonascimento: state.student?.date_of_birth
        ? stringToDate(state.student.date_of_birth)
        : undefined,
    }

    let formattedData: IRegisterStudent

    if (formData?.pe_code) {
      const friend = await getPeFriend(formData?.pe_code)
      formattedData = formatData({
        ...dataStudent,
        friend_pe_id: friend.id,
      }) as unknown as IRegisterStudent
    } else {
      formattedData = formatData(dataStudent) as unknown as IRegisterStudent
    }

    toast.promise(registerStudent(formattedData), {
      pending: 'Realizando inscrição...',
      success: 'Aluno inscrito!',
      error,
    })
    setLoader(false)
  }

  const handleToggleTermsOfUse = () => setOpenTerms(true)

  const handleCloseModal = (accepted: boolean) => {
    setOpenTerms(false)
    setAcceptedTerms(accepted)
  }

  return (
    <>
      <Header />

      {isSubmitting && loader && <Loader />}

      {openTerms && (
        <Modal.Root title="Termos de uso" content={USE_TERMS}>
          <Modal.ButtonRoot>
            <Modal.Button
              labelText="Não concordo"
              handleClickButton={() => handleCloseModal(false)}
            />
            <Modal.Button
              labelText="Concordo"
              handleClickButton={() => handleCloseModal(true)}
              isPrimary
            />
          </Modal.ButtonRoot>
        </Modal.Root>
      )}

      <form
        onSubmit={handleSubmit(handleOnSubmit)}
        className={styles.container}
      >
        <Statusbar step="step_4" />

        <span>Etapa 4 de 4</span>
        <div className={styles.content_form}>
          <TitleFormStudents title="Pesquisa" />
          <div className={styles.double_input}>
            <Input
              text="Qual sua renda familiar"
              name="family_income"
              register={{ ...register('family_income') }}
              placeholder="Insira sua renda familiar"
              onKeyUp={handleKeyUpMoney}
              errors={errors?.family_income}
            />
            <Input
              text="Código do amigo PE"
              name="pe_code"
              register={{ ...register('pe_code') }}
              placeholder="Insira o código de indicação do amigo PE"
              errors={errors?.pe_code}
            />
          </div>

          <div className={styles.use_terms}>
            <Select
              question="Como conheceu nosso programa?"
              name="social_media"
              register={{ ...register('social_media') }}
            />

            <Checkbox
              name="use_terms"
              checked={acceptedTerms}
              register={{ ...register('use_terms') }}
              onClick={handleToggleTermsOfUse}
              left_text="Aceitar termos de uso."
            />
          </div>

          <FooterButtons
            redirectLink={`/register-student/${course_id}/step-3`}
            disabled={!acceptedTerms}
          />
        </div>
      </form>
    </>
  )
}
